import { Controller } from "@hotwired/stimulus"

// メニュー管理画面のカテゴリ開閉を管理する
// 開閉状態は localStorage に保存して、並び替え（Turbo Streamでツリー丸ごと再描画）や
// リロード後も「ユーザーが最後に操作した状態」を復元する。デフォルトは全部閉じた状態。
export default class extends Controller {
  static targets = ["section", "toggle"]
  static values = { shopId: String }

  connect() {
    this.expandedIds = this.loadExpandedIds()
  }

  // Turbo Streamでツリーが差し替わっても、新しいDOMに対してここが呼ばれるので状態を復元できる
  sectionTargetConnected(section) {
    this.applyState(section)
  }

  // カテゴリ見出しのボタンが（編集キャンセル等で）差し替わったときも、アイコンの向きを状態に合わせる
  toggleTargetConnected(button) {
    const section = this.findSection(button.dataset.sectionId)
    if (section) this.applyState(section)
  }

  toggleSection(event) {
    // 検索中は中身が常に表示されている（.is-searching）ので、開閉を変えても見た目に出ない。保存だけされて混乱するので無視する
    if (this.element.classList.contains("is-searching")) return

    const section = this.findSection(event.currentTarget.dataset.sectionId)
    if (!section) return
    this.expandedIds ??= this.loadExpandedIds()

    const categoryId = section.dataset.categoryId
    this.setExpanded(section, !this.expandedIds.has(categoryId))
  }

  // メニュー追加・編集後のスクロール先が閉じたカテゴリの中にあるときに開く（scroll-toから発火）
  expandFromEvent(event) {
    const categoryId = event.detail?.categoryId
    if (!categoryId) return

    const section = this.sectionTargets.find(s => s.dataset.categoryId === String(categoryId))
    if (section) this.setExpanded(section, true)
  }

  jump(event) {
    event.preventDefault()
    const targetId = event.currentTarget.getAttribute("href").slice(1)
    const target = document.getElementById(targetId)
    if (!target) return

    // ジャンプ先のカテゴリは開いて中身を見せる
    const section = this.findSection(targetId)
    if (section) this.setExpanded(section, true)

    target.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // --- private ---

  setExpanded(section, expanded) {
    this.expandedIds ??= this.loadExpandedIds()
    const categoryId = section.dataset.categoryId
    if (expanded) {
      this.expandedIds.add(categoryId)
    } else {
      this.expandedIds.delete(categoryId)
    }
    this.saveExpandedIds()
    this.applyState(section)
  }

  applyState(section) {
    // targetConnected は connect() より先に呼ばれることがあるので、ここでも初期化しておく
    this.expandedIds ??= this.loadExpandedIds()
    const expanded = this.expandedIds.has(section.dataset.categoryId)
    const content = this.ownCollapseContent(section)
    if (content) content.classList.toggle("hidden", !expanded)

    const icon = this.ownToggleIcon(section)
    if (icon) icon.classList.toggle("-rotate-90", !expanded)
  }

  findSection(sectionId) {
    return this.sectionTargets.find(s => s.id === sectionId)
  }

  // 子カテゴリの要素を拾わないように、そのセクション自身に属するものだけ返す
  ownCollapseContent(section) {
    return [...section.querySelectorAll("[data-collapse-content]")]
      .find(el => el.closest("[data-category-id]") === section)
  }

  ownToggleIcon(section) {
    const button = section.querySelector(`[data-section-id="${section.id}"]`)
    return button?.querySelector("[data-collapse-icon]")
  }

  storageKey() {
    return `revimenu:manage:menu:expanded:${this.shopIdValue || "default"}`
  }

  loadExpandedIds() {
    try {
      const raw = window.localStorage.getItem(this.storageKey())
      const ids = raw ? JSON.parse(raw) : []
      return new Set(Array.isArray(ids) ? ids.map(String) : [])
    } catch {
      return new Set()
    }
  }

  saveExpandedIds() {
    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify([...this.expandedIds]))
    } catch {
      // プライベートモード等で保存できなくても画面上の開閉は動くのでそのまま
    }
  }
}
